/**
 * DNS Analytics — DNS query volume for a managed-DNS zone (Owl `GetDNSQueryDiagram`).
 * Zone-scoped via the shared scope bar; 7/14/30-day or custom range; Summary
 * (one line) vs Divide (one line per region, `group_regions=true`).
 */
import { useMemo, useState } from 'react';
import { Network } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useWafDnsQueryDiagram } from '@/api/waf';
import { ChartCard, RefreshButton, Stack } from '../../_shared/AnalyticsKit';
import { formatCount } from '../../_shared/analytics-format';
import { WafScopeBar, type WafScope } from './WafScopeBar';
import { WafRangeControl, type ResolvedRange } from './WafRangeControl';
import { intervalForRange, dnsRows, type ChartMode } from './diagram-transform';
import { loadJSON, saveJSON } from './storage';
import styles from './WafAnalytics.module.css';

export function WafDnsAnalytics({ serviceId }: { serviceId: string }) {
  const modeKey = `waf:${serviceId}:dns:mode`;
  const [scope, setScope] = useState<WafScope>({});
  const [range, setRange] = useState<ResolvedRange | null>(null);
  const [mode, setMode] = useState<ChartMode>(() => loadJSON<ChartMode>(modeKey, 'summary'));

  const zoneId = scope.zone?.id;
  // "All site" → omit site_id so the BE returns the org-wide overview.
  const hasScope = !!zoneId || !!scope.allSites;
  const interval = range ? intervalForRange(range.start_time, range.end_time) : 86400;

  // Single API call — mode (Summary line vs Divide area) is render-only, not a param.
  const q = useWafDnsQueryDiagram(
    serviceId,
    {
      site_id: zoneId,
      start_time: range?.start_time ?? 0,
      end_time: range?.end_time ?? 0,
      interval,
    },
    hasScope && !!range,
  );

  const { rows, series } = useMemo(() => dnsRows(q.data, interval), [q.data, interval]);

  const changeMode = (m: ChartMode) => {
    setMode(m);
    saveJSON(modeKey, m);
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<Network size={22} />}
        title="DNS Requests"
        description="DNS query volume for the selected managed-DNS zone."
      />

      <div className={styles.toolbar}>
        <WafScopeBar serviceId={serviceId} mode="zone" onChange={setScope} />
        <div className={styles.controls}>
          <WafRangeControl serviceId={serviceId} pageKey="dns" onChange={setRange} />
          <div className={styles.controlsRight}>
            <Select value={mode} onValueChange={(v) => changeMode(v as ChartMode)}>
              <SelectTrigger aria-label="Chart mode" className={styles.modeSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Summary</SelectItem>
                <SelectItem value="divide">Divide</SelectItem>
              </SelectContent>
            </Select>
            <RefreshButton onRefresh={() => q.refetch()} />
          </div>
        </div>
      </div>

      <Stack>
        <div className={mode === 'divide' ? styles.denseFill : undefined}>
          <ChartCard
            title="DNS queries"
            description="Query volume over the selected range."
            data={rows}
            xKey="t"
            series={series}
            type={mode === 'divide' ? 'area' : 'line'}
            height={280}
            stacked={mode === 'divide'}
            showLegend={false}
            valueFormatter={(v) => formatCount(Number(v))}
            loading={q.isLoading}
          />
        </div>
      </Stack>
    </Container>
  );
}

export default WafDnsAnalytics;
